/* eslint-disable react/prop-types */
import React, { useEffect, useState } from 'react';
import {
  Divider,
  List,
  Typography,
  Tooltip,
  Skeleton,
  Select,
  Form,
  notification,
  Button,
  Table,
  Input,
  Radio,
} from 'antd';
import {
  EyeOutlined,
  SearchOutlined,
  CloseOutlined,
  CheckOutlined,
  PlusOutlined,
  DeleteOutlined,
  EditOutlined,
} from '@ant-design/icons';
import { Truck, X } from 'lucide-react';
import { Spinner, Button as CrceButton } from '@crc-evans/ui-components';
import './ShipmentDashBoardPage.css';
import ShipmentLogs from './ShipmentLogs';
import {
  SHIPMENT_PRIORITY_OPTIONS,
  SHIPMENT_CATEGORY_OPTIONS,
  SHIPMENT_PACKAGING_OPTIONS,
  normalizeShipmentMetaFromRecord,
  packagingFormValueToApi,
} from './shipmentFieldOptions';
import {
  OnDeleteShipmentNumber,
  OnFetchShipmentSno,
  OnAddShipmentNumber,
  OnPreviewShipmentNumber,
  OnAddorUpdateShipmentNumber,
} from '../../redux/ShipmentDetails/saga';

const { Title } = Typography;
const { Option } = Select;

const ShipmentNo = (props) => {
  const { selectedData } = props;

  const [searchOpen, setSearchOpen] = useState(false);
  const [searchSno, setSearchSno] = useState(false);
  const [equipSnoDrop, setEquipSnoDrop] = useState([]);
  const [equipSnoList, setEquipSnoList] = useState([]);
  const [equipSnoSearchForm] = Form.useForm();
  const [isTableModelVisible, setIsTableModelVisible] = useState(false);
  const [isDeleteModalVisible, setIsDeleteModalVisible] = useState(false);
  const [deleteLoading, setDeleteLoading] = useState(false);
  const [selectedDataSno, setSelectedDataSno] = useState('');
  const [fetchLoading, setFetchLoading] = useState(false);
  const [isConfirmModalVisible, setIsConfirmModalVisible] = useState(false);
  const [previewShipmentNumber, setPreviewShipmentNumber] = useState('');
  const [addLoading, setAddLoading] = useState(false);
  
  // UI Migration State
  const [numSearch, setNumSearch] = useState('');
  const [filteredData, setFilteredData] = useState([]);

  // Main Branch State
  const [isEditModalVisible, setIsEditModalVisible] = useState(false);
  const [editShipmentNumber, setEditShipmentNumber] = useState('');
  const [editLoading, setEditLoading] = useState(false);
  const [editPriority, setEditPriority] = useState('medium');
  const [editCategory, setEditCategory] = useState('project');
  const [editPackaging, setEditPackaging] = useState(undefined);

  const [confirmPriority, setConfirmPriority] = useState('medium');
  const [confirmCategory, setConfirmCategory] = useState('project');
  const [confirmPackaging, setConfirmPackaging] = useState(undefined);

  // Helper for rendering searchable column titles matching Heat Profile style
  const renderColumnTitle = (title, onSearch, width = '100%') => (
    <div className="column-header-with-filter">
      <div className="column-header-text-wrapper">
        <span>{title}</span>
      </div>
      <div className="header-filter-input">
        <Input
          placeholder="Search"
          prefix={<SearchOutlined style={{ color: '#94a3b8', fontSize: '12px' }} />}
          onChange={(e) => onSearch(e.target.value)}
          onClick={(e) => e.stopPropagation()}
          style={{ width: width === '100%' ? '140px' : width }}
          size="small"
          allowClear
        />
      </div>
    </div>
  );

  // Local filtering logic
  useEffect(() => {
    let data = equipSnoDrop || [];
    if (numSearch) {
      data = data.filter(item =>
        item.shipmentNumber?.toLowerCase().includes(numSearch.toLowerCase())
      );
    }
    setEquipSnoList(data);
  }, [equipSnoDrop, numSearch]);

  useEffect(() => {
    if (selectedData) OnfetchData();
  }, [selectedData]);

  useEffect(() => {
    if (selectedData) {
      const fetchShippingForms = async () => {
        try {
          setFetchLoading(true);
          await OnfetchData();
          setFetchLoading(false);
        } catch (error) {
          console.error('Failed to fetch shipping forms:', error);
          setFetchLoading(false);
        }
      };

      fetchShippingForms();
    }
  }, [selectedData]);

  const handleEyeClick = async (record) => {
    setSelectedDataSno(record);
    setIsTableModelVisible(true);
  };

  const OnfetchData = async () => {
    const obj = {
      id: selectedData.id,
    };
    const res = await OnFetchShipmentSno(obj);
    if (res['shipmentType']?.shipments && res['shipmentType'].shipments.length > 0) {
      setEquipSnoList(res['shipmentType'].shipments);
      setEquipSnoDrop(res['shipmentType'].shipments);
    }
  };

  const showDeleteModal = (record) => {
    setSelectedDataSno(record);
    setIsDeleteModalVisible(true);
  };

  const openEditShipmentNumber = (record) => {
    setSelectedDataSno(record);
    setEditShipmentNumber(record.shipmentNumber || '');
    const meta = normalizeShipmentMetaFromRecord(record);
    setEditPriority(meta.priority);
    setEditCategory(meta.shipmentCategory);
    setEditPackaging(meta.packagingSubcategory);
    setIsEditModalVisible(true);
  };

  const handleEditOk = async () => {
    const trimmed = String(editShipmentNumber || '').trim();
    if (!trimmed) {
      notification.warning({ message: 'Please enter a shipment number.' });
      return;
    }
    if (!editPackaging) {
      notification.warning({ message: 'Please select packaging (subcategory).' });
      return;
    }
    setEditLoading(true);
    const obj = {
      id: selectedDataSno.id,
      data: {
        shipment: {
          shipmentTypeId: selectedData.id,
          shipmentNumber: trimmed,
          priority: editPriority,
          shipmentCategory: editCategory,
          packagingType: packagingFormValueToApi(editPackaging),
        },
      },
    };
    const res = await OnAddorUpdateShipmentNumber(obj, true);
    if (res) await OnfetchData();
    setEditLoading(false);
    setIsEditModalVisible(false);
    setSelectedDataSno('');
  };

  const handleEditCancel = () => {
    setIsEditModalVisible(false);
    setSelectedDataSno('');
    setEditShipmentNumber('');
    setEditPackaging(undefined);
  };

  const handleOk = async () => {
    setDeleteLoading(true);
    await OnDeleteShipmentNumber(selectedDataSno);
    await OnfetchData();
    setDeleteLoading(false);
    setIsDeleteModalVisible(false);
  };

  const handleCancel = () => {
    setIsDeleteModalVisible(false);
    setSelectedDataSno('');
  };

  // Step 1: Initial click to preview shipment number
  const handleAddButtonClick = async () => {
    if (!selectedData || !selectedData.id) {
      notification.error({
        message: 'Cannot add shipment number',
        description: 'No shipment type selected',
      });
      return;
    }

    setAddLoading(true);

    try {
      // Call the preview endpoint to get the next number
      const previewData = await OnPreviewShipmentNumber(selectedData.id);

      if (previewData && previewData.previewShipmentNumber) {
        setPreviewShipmentNumber(previewData.previewShipmentNumber);
        setConfirmPriority('medium');
        setConfirmCategory('project');
        setConfirmPackaging(undefined);
        setIsConfirmModalVisible(true);
      } else {
        notification.error({
          message: 'Failed to generate shipment number preview',
        });
      }
    } catch (error) {
      notification.error({
        message: 'Error generating shipment number',
        description: error?.message || 'Unknown error',
      });
    } finally {
      setAddLoading(false);
    }
  };

  // Step 2: Handle confirmation (Yes/No)
  const handleConfirmAdd = async () => {
    if (!confirmPackaging) {
      notification.warning({ message: 'Please select packaging (subcategory).' });
      return;
    }
    const num = String(previewShipmentNumber || '').trim();
    if (!num) {
      notification.warning({ message: 'Please enter a shipment number.' });
      return;
    }
    setAddLoading(true);

    const payload = {
      shipment: {
        shipmentTypeId: selectedData.id,
        shipmentNumber: num,
        priority: confirmPriority,
        shipmentCategory: confirmCategory,
        packagingType: packagingFormValueToApi(confirmPackaging),
      },
    };

    const response = await OnAddShipmentNumber(payload);

    if (response?.shipment?.shipmentNumber) {
      notification.success({
        message: `Shipment number ${response.shipment.shipmentNumber} added successfully`,
      });
    }

    // Close the modal
    setIsConfirmModalVisible(false);

    await OnfetchData();
    setAddLoading(false);
  };

  const handleCancelAdd = () => {
    setIsConfirmModalVisible(false);
    setPreviewShipmentNumber('');
    setConfirmPackaging(undefined);
  };

  const onSelectEquipSno = () => {
    setSearchSno(true);
  };

  const onSearchEquipmentSno = () => {
    equipSnoSearchForm.validateFields().then(async (data) => {
      if (data && data.shipmentNumber) {
        setSearchSno(false);
        const newArray = equipSnoDrop.find((u) => u.shipmentNumber === data.shipmentNumber);
        setEquipSnoList([newArray]);
      } else {
        notification.warning({
          message: 'Select Equipment Sno to Search.',
        });
      }
    });
  };

  const columns = [
    {
      title: renderColumnTitle('Shipment Number', setNumSearch, '180px'),
      dataIndex: 'shipmentNumber',
      key: 'shipmentNumber',
      sorter: (a, b) => a.shipmentNumber.localeCompare(b.shipmentNumber),
    },
    {
      title: 'Actions',
      key: 'actions',
      width: '120px',
      align: 'center',
      render: (text, record) => (
        <div className="heat-profile-actions-cell" style={{ display: 'flex', gap: '12px', justifyContent: 'center' }}>
          <Tooltip title="View History">
            <EyeOutlined
              className="txtIcon"
              style={{ color: '#00A86B', fontSize: '18px', cursor: 'pointer' }}
              onClick={() => handleEyeClick(record)}
            />
          </Tooltip>
          <Tooltip title="Edit shipment number">
            <EditOutlined
              className="txtIcon"
              style={{ color: '#1890ff', fontSize: '18px', cursor: 'pointer' }}
              onClick={() => openEditShipmentNumber(record)}
            />
          </Tooltip>
          <Tooltip title="Delete">
            <DeleteOutlined
              className="txtIcon"
              style={{ color: '#ef314c', fontSize: '18px', cursor: 'pointer' }}
              onClick={() => showDeleteModal(record)}
            />
          </Tooltip>
        </div>
      ),
    },
  ];

  return (
    <div className="heat-profile-card" style={{ marginTop: '20px' }}>
      <div className="heat-profile-list-header">
        <div className="heat-profile-header-left">
          <div className="heat-profile-icon-wrap">
            <Truck size={20} color="#ef314c" />
          </div>
          <div className="heat-profile-header-content">
            <Title level={4} className="heat-profile-list-title">Shipment Numbers</Title>
            <p className="heat-profile-list-description">Manage individual shipment units for this type.</p>
          </div>
        </div>

        <div className="heat-profile-header-actions">
          <Button
            type="primary"
            icon={<PlusOutlined />}
            onClick={handleAddButtonClick}
            className="heat-profile-add-btn"
          >
            Add Number
          </Button>
        </div>
      </div>

      <div className="heat-profile-table-wrap">
        <Table
          className="heat-profile-table"
          columns={columns}
          dataSource={equipSnoList}
          rowKey="id"
          loading={{
            spinning: fetchLoading,
            indicator: <Spinner variant="inline" size="large" color="#00C9A7" />,
          }}
          pagination={{ pageSize: 10 }}
          size="middle"
        />
      </div>

      {isTableModelVisible && (
        <ShipmentLogs
          showFormModel={isTableModelVisible}
          setShowFormModel={setIsTableModelVisible}
          selectedData={selectedDataSno}
        />
      )}

      {isConfirmModalVisible && (
        <div className="mc-dpr-modal-backdrop" role="presentation">
          <div
            className="mc-dpr-modal mc-dpr-modal--wide"
            role="dialog"
            aria-labelledby="ship-no-confirm-title"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="mc-dpr-modal-head">
              <h3 id="ship-no-confirm-title" className="mc-dpr-modal-title">
                Confirm new shipment number
              </h3>
              <button type="button" className="mc-dpr-modal-close" aria-label="Close" onClick={handleCancelAdd}>
                <X size={20} />
              </button>
            </div>
            <div className="mc-dpr-modal-body">
              <p className="shipment-modal-hint" style={{ marginBottom: '16px', textAlign: 'center' }}>
                Review details below, then confirm to create this shipment number.
              </p>
              <Form layout="vertical" colon={false}>
                <div className="mc-dpr-modal-form-grid">
                  <div className="mc-dpr-modal-form-row">
                    <div className="mc-dpr-modal-field">
                      <label htmlFor="ship-no-confirm-priority">
                        Priority <span className="required">*</span>
                      </label>
                      <Form.Item style={{ marginBottom: 0 }}>
                        <Select
                          id="ship-no-confirm-priority"
                          className="material-select"
                          size="large"
                          placeholder="Select priority"
                          allowClear
                          value={confirmPriority || undefined}
                          onChange={setConfirmPriority}
                          getPopupContainer={(trigger) => trigger.parentNode}
                          dropdownClassName="heat-profile-select-dropdown mc-dpr-modal-select-dd"
                        >
                          {SHIPMENT_PRIORITY_OPTIONS.map((o) => (
                            <Option key={o.value} value={o.value}>{o.label}</Option>
                          ))}
                        </Select>
                      </Form.Item>
                    </div>
                  </div>
                  <div className="mc-dpr-modal-form-row">
                    <div className="mc-dpr-modal-field">
                      <label htmlFor="ship-no-confirm-packaging">
                        Subcategory (packaging) <span className="required">*</span>
                      </label>
                      <Form.Item style={{ marginBottom: 0 }}>
                        <Select
                          id="ship-no-confirm-packaging"
                          className="material-select"
                          size="large"
                          placeholder="Select packaging type"
                          allowClear
                          value={confirmPackaging || undefined}
                          onChange={setConfirmPackaging}
                          getPopupContainer={(trigger) => trigger.parentNode}
                          dropdownClassName="heat-profile-select-dropdown mc-dpr-modal-select-dd"
                        >
                          {SHIPMENT_PACKAGING_OPTIONS.map((o) => (
                            <Option key={o.value} value={o.value}>{o.label}</Option>
                          ))}
                        </Select>
                      </Form.Item>
                    </div>
                  </div>
                  <div className="mc-dpr-modal-form-row mc-dpr-modal-form-row--span-2">
                    <div className="mc-dpr-modal-field">
                      <span className="mc-dpr-modal-field-label">
                        Category <span className="required">*</span>
                      </span>
                      <Form.Item style={{ marginBottom: 0 }}>
                        <Radio.Group
                          className="shipment-category-radio-group"
                          value={confirmCategory}
                          onChange={(e) => setConfirmCategory(e.target.value)}
                        >
                          {SHIPMENT_CATEGORY_OPTIONS.map((o) => (
                            <Radio key={o.value} value={o.value}>{o.label}</Radio>
                          ))}
                        </Radio.Group>
                      </Form.Item>
                    </div>
                  </div>
                  <div className="mc-dpr-modal-form-row mc-dpr-modal-form-row--span-2">
                    <div className="mc-dpr-modal-field">
                      <label htmlFor="ship-no-confirm-number">
                        Shipment number <span className="required">*</span>
                      </label>
                      <Form.Item style={{ marginBottom: 0 }}>
                        <Input
                          id="ship-no-confirm-number"
                          value={previewShipmentNumber}
                          onChange={(e) => setPreviewShipmentNumber(e.target.value)}
                        />
                      </Form.Item>
                    </div>
                  </div>
                </div>
              </Form>
            </div>
            <div className="mc-dpr-modal-foot">
              <CrceButton label="Cancel" variant="tertiary" size="medium" onClick={handleCancelAdd} />
              <CrceButton
                label={addLoading ? 'Creating…' : 'Confirm create'}
                variant="primary"
                size="medium"
                onClick={handleConfirmAdd}
                disabled={addLoading || !confirmPackaging || !String(previewShipmentNumber || '').trim()}
              />
            </div>
          </div>
        </div>
      )}

      {isEditModalVisible && (
        <div className="mc-dpr-modal-backdrop" role="presentation">
          <div
            className="mc-dpr-modal mc-dpr-modal--wide"
            role="dialog"
            aria-labelledby="ship-no-edit-title"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="mc-dpr-modal-head">
              <h3 id="ship-no-edit-title" className="mc-dpr-modal-title">
                Edit shipment number
              </h3>
              <button type="button" className="mc-dpr-modal-close" aria-label="Close" onClick={handleEditCancel}>
                <X size={20} />
              </button>
            </div>
            <div className="mc-dpr-modal-body">
              <Form layout="vertical" colon={false}>
                <div className="mc-dpr-modal-form-grid">
                  <div className="mc-dpr-modal-form-row">
                    <div className="mc-dpr-modal-field">
                      <label htmlFor="ship-no-edit-priority">
                        Priority <span className="required">*</span>
                      </label>
                      <Form.Item style={{ marginBottom: 0 }}>
                        <Select
                          id="ship-no-edit-priority"
                          className="material-select"
                          size="large"
                          placeholder="Select priority"
                          allowClear
                          value={editPriority || undefined}
                          onChange={setEditPriority}
                          getPopupContainer={(trigger) => trigger.parentNode}
                          dropdownClassName="heat-profile-select-dropdown mc-dpr-modal-select-dd"
                        >
                          {SHIPMENT_PRIORITY_OPTIONS.map((o) => (
                            <Option key={o.value} value={o.value}>{o.label}</Option>
                          ))}
                        </Select>
                      </Form.Item>
                    </div>
                  </div>
                  <div className="mc-dpr-modal-form-row">
                    <div className="mc-dpr-modal-field">
                      <label htmlFor="ship-no-edit-packaging">
                        Subcategory (packaging) <span className="required">*</span>
                      </label>
                      <Form.Item style={{ marginBottom: 0 }}>
                        <Select
                          id="ship-no-edit-packaging"
                          className="material-select"
                          size="large"
                          placeholder="Select packaging type"
                          allowClear
                          value={editPackaging || undefined}
                          onChange={setEditPackaging}
                          getPopupContainer={(trigger) => trigger.parentNode}
                          dropdownClassName="heat-profile-select-dropdown mc-dpr-modal-select-dd"
                        >
                          {SHIPMENT_PACKAGING_OPTIONS.map((o) => (
                            <Option key={o.value} value={o.value}>{o.label}</Option>
                          ))}
                        </Select>
                      </Form.Item>
                    </div>
                  </div>
                  <div className="mc-dpr-modal-form-row mc-dpr-modal-form-row--span-2">
                    <div className="mc-dpr-modal-field">
                      <span className="mc-dpr-modal-field-label">
                        Category <span className="required">*</span>
                      </span>
                      <Form.Item style={{ marginBottom: 0 }}>
                        <Radio.Group
                          className="shipment-category-radio-group"
                          value={editCategory}
                          onChange={(e) => setEditCategory(e.target.value)}
                        >
                          {SHIPMENT_CATEGORY_OPTIONS.map((o) => (
                            <Radio key={o.value} value={o.value}>{o.label}</Radio>
                          ))}
                        </Radio.Group>
                      </Form.Item>
                    </div>
                  </div>
                  <div className="mc-dpr-modal-form-row mc-dpr-modal-form-row--span-2">
                    <div className="mc-dpr-modal-field">
                      <label htmlFor="ship-no-edit-number">
                        Shipment number <span className="required">*</span>
                      </label>
                      <Form.Item style={{ marginBottom: 0 }}>
                        <Input
                          id="ship-no-edit-number"
                          value={editShipmentNumber}
                          onChange={(e) => setEditShipmentNumber(e.target.value)}
                        />
                      </Form.Item>
                    </div>
                  </div>
                </div>
              </Form>
            </div>
            <div className="mc-dpr-modal-foot">
              <CrceButton label="Cancel" variant="tertiary" size="medium" onClick={handleEditCancel} />
              <CrceButton
                label={editLoading ? 'Updating…' : 'Update'}
                variant="primary"
                size="medium"
                onClick={handleEditOk}
                disabled={editLoading || !editPackaging}
              />
            </div>
          </div>
        </div>
      )}

      {isDeleteModalVisible && (
        <div className="mc-dpr-modal-backdrop" role="presentation">
          <div
            className="mc-dpr-modal mc-dpr-modal--narrow mc-dpr-modal--delete"
            role="dialog"
            aria-labelledby="ship-no-delete-title"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="mc-dpr-modal-head">
              <h3 id="ship-no-delete-title" className="mc-dpr-modal-title">
                Delete shipment number
              </h3>
              <button
                type="button"
                className="mc-dpr-modal-close"
                aria-label="Close"
                disabled={deleteLoading}
                onClick={handleCancel}
              >
                <X size={20} />
              </button>
            </div>
            <div className="mc-dpr-modal-body">
              <div className="mc-dpr-modal-delete-body">
                <p className="mc-dpr-modal-delete-lead">
                  Deleting this shipment number removes all corresponding stations and related information.
                </p>
                <p className="mc-dpr-modal-delete-target">{selectedDataSno?.shipmentNumber}</p>
                <p className="mc-dpr-modal-delete-warning">This action cannot be undone.</p>
              </div>
            </div>
            <div className="mc-dpr-modal-foot">
              <CrceButton label="Cancel" variant="tertiary" size="medium" onClick={handleCancel} disabled={deleteLoading} />
              <span className="mc-delete-confirm-wrap">
                <CrceButton
                  label={deleteLoading ? 'Deleting…' : 'Delete'}
                  variant="primary"
                  size="medium"
                  onClick={handleOk}
                  disabled={deleteLoading}
                />
              </span>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ShipmentNo;
